import "@/App.css";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Flame, 
  Cpu, 
  Smartphone, 
  PiggyBank, 
  Heart, 
  Leaf, 
  Menu, 
  X, 
  ChevronRight,
  Check,
  Send,
  Mail,
  MapPin
} from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

// Navbar Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: "#problem", label: "The Problem" },
    { href: "#solution", label: "Solution" },
    { href: "#how-it-works", label: "How It Works" },
    { href: "#contact", label: "Contact" }
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
      data-testid="navbar"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#" className="flex items-center" data-testid="logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_cleanfuel-ug/artifacts/2wwu57y3_GASIFY_LOGO-2.png" 
              alt="Gasify Logo" 
              className="h-10 md:h-12"
            />
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-slate-600 hover:text-gasify-orange transition-colors font-medium"
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </a>
            ))}
            <a
              href="#contact"
              className="bg-gasify-orange text-white px-6 py-2.5 rounded-full font-semibold hover:bg-gasify-orange-hover transition-all btn-primary-shadow hover:-translate-y-0.5"
              data-testid="nav-partner-btn"
            >
              Partner With Us
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Nav */}
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-white rounded-2xl shadow-lg p-6 mt-2"
          >
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="block py-3 text-slate-600 hover:text-gasify-orange transition-colors font-medium"
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a
              href="#contact"
              className="block mt-4 bg-gasify-orange text-white px-6 py-3 rounded-full font-semibold text-center"
              onClick={() => setIsOpen(false)}
            >
              Partner With Us
            </a>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => (
  <section 
    id="hero" 
    className="min-h-screen bg-warm-gradient pt-20 flex items-center"
    data-testid="hero-section"
  >
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center py-8 md:py-12">
        {/* Left Content */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="order-2 lg:order-1 text-center lg:text-left"
        >
          <motion.p 
            variants={fadeUp}
            className="text-xs md:text-sm text-gasify-orange uppercase tracking-widest font-semibold mb-3 md:mb-4"
          >
            Clean Cooking for Uganda
          </motion.p>
          <motion.h1 
            variants={fadeUp}
            className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gasify-blue leading-tight mb-4 md:mb-6"
          >
            Clean Cooking,
            <br />
            <span className="text-gasify-orange">Pay As You Cook</span>
          </motion.h1>
          <motion.p 
            variants={fadeUp}
            className="text-base md:text-lg lg:text-xl text-slate-600 leading-relaxed mb-6 md:mb-8 max-w-lg mx-auto lg:mx-0"
          >
            Gasify brings affordable, safe, and clean LPG to every Ugandan kitchen using smart meter technology.
          </motion.p>
          <motion.div 
            variants={fadeUp}
            className="flex justify-center lg:justify-start"
          >
            <a
              href="#contact"
              className="inline-flex items-center justify-center gap-2 bg-gasify-orange text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold hover:bg-gasify-orange-hover transition-all btn-primary-shadow hover:-translate-y-1 text-sm md:text-base"
              data-testid="hero-partner-btn"
            >
              Partner With Us
              <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
            </a>
          </motion.div>
        </motion.div>

        {/* Right Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="relative order-1 lg:order-2"
        >
          <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-xl md:shadow-2xl">
            <img
              src="https://customer-assets.emergentagent.com/job_cleanfuel-ug/artifacts/tonnnqa6_WhatsApp%20Image%202026-02-28%20at%2007.44.33.jpeg"
              alt="African woman cooking with firewood and charcoal"
              className="w-full h-[280px] sm:h-[350px] md:h-[400px] lg:h-[500px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gasify-blue/30 to-transparent" />
          </div>
          {/* Floating Stats Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.8 }}
            className="absolute -left-2 md:-left-8 bottom-4 md:bottom-8 bg-white rounded-xl md:rounded-2xl shadow-lg md:shadow-xl p-4 md:p-6"
          >
            <div className="flex items-center gap-3 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gasify-orange-light rounded-full flex items-center justify-center">
                <Flame className="w-5 h-5 md:w-6 md:h-6 text-gasify-orange" />
              </div>
              <div>
                <p className="text-2xl md:text-3xl font-bold text-gasify-blue">90%</p>
                <p className="text-xs md:text-sm text-slate-500">Use biomass daily</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  </section>
);

// Problem Section
const ProblemSection = () => (
  <section 
    id="problem" 
    className="py-16 md:py-20 lg:py-32 bg-slate-900 text-white relative overflow-hidden"
    data-testid="problem-section"
  >
    {/* Background Image */}
    <div 
      className="absolute inset-0 opacity-30"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1516594798947-e65505dbb29d?w=1200&q=80')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    />
    <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/95 to-slate-900/80" />
    
    <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        {/* Content */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center lg:text-left"
        >
          <motion.p 
            variants={fadeUp}
            className="text-xs md:text-sm text-gasify-orange uppercase tracking-widest font-semibold mb-3 md:mb-4"
          >
            The Challenge
          </motion.p>
          <motion.h2 
            variants={fadeUp}
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-4 md:mb-6"
          >
            Uganda's Cooking Crisis
          </motion.h2>
          <motion.p 
            variants={fadeUp}
            className="text-base md:text-lg text-slate-300 leading-relaxed mb-6 md:mb-8"
          >
            Over 90% of Ugandan households still rely on charcoal and firewood for cooking, 
            causing severe health issues and environmental damage.
          </motion.p>
          
          <motion.div 
            variants={staggerContainer}
            className="space-y-3 md:space-y-4"
          >
            {[
              { icon: Heart, text: "4 million deaths annually from indoor air pollution" },
              { icon: Leaf, text: "Massive deforestation and environmental damage" },
              { icon: PiggyBank, text: "High upfront costs block LPG adoption" }
            ].map((item, index) => (
              <motion.div 
                key={index}
                variants={fadeUp}
                className="flex items-start gap-3 md:gap-4 text-left"
              >
                <div className="w-9 h-9 md:w-10 md:h-10 bg-gasify-orange/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-4 h-4 md:w-5 md:h-5 text-gasify-orange" />
                </div>
                <p className="text-sm md:text-base text-slate-300">{item.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid grid-cols-2 gap-3 md:gap-6"
        >
          {[
            { value: "90%", label: "Use biomass fuel" },
            { value: "$100+", label: "Upfront LPG cost" },
            { value: "70%", label: "Below poverty line" },
            { value: "200K+", label: "Food vendors affected" }
          ].map((stat, index) => (
            <motion.div
              key={index}
              variants={fadeUp}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 text-center hover:bg-white/10 transition-colors"
            >
              <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-gasify-orange mb-1 md:mb-2">{stat.value}</p>
              <p className="text-xs md:text-sm text-slate-400">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  </section>
);

// Solution Section
const SolutionSection = () => (
  <section 
    id="solution" 
    className="py-16 md:py-20 lg:py-32 bg-white"
    data-testid="solution-section"
  >
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
        className="text-center mb-10 md:mb-16"
      >
        <motion.p 
          variants={fadeUp}
          className="text-xs md:text-sm text-gasify-orange uppercase tracking-widest font-semibold mb-3 md:mb-4"
        >
          Our Solution
        </motion.p>
        <motion.h2 
          variants={fadeUp}
          className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-gasify-blue mb-4 md:mb-6"
        >
          Smart Cooking, <span className="text-gasify-orange">Simple Payments</span>
        </motion.h2>
        <motion.p 
          variants={fadeUp}
          className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto"
        >
          Our Pay-as-you-cook technology makes clean energy accessible to everyone.
        </motion.p>
      </motion.div>

      {/* Bento Grid */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
        className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6"
      >
        {/* Smart Meter Card - Large */}
        <motion.div 
          variants={fadeUp}
          className="md:col-span-2 bg-gradient-to-br from-gasify-blue to-gasify-blue-hover rounded-2xl md:rounded-3xl p-6 md:p-8 text-white relative overflow-hidden group"
        >
          <div className="relative z-10">
            <div className="w-12 h-12 md:w-14 md:h-14 bg-white/20 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6">
              <Cpu className="w-6 h-6 md:w-8 md:h-8" />
            </div>
            <h3 className="font-heading text-xl md:text-2xl lg:text-3xl font-semibold mb-3 md:mb-4">Gasify Smart Meter</h3>
            <p className="text-white/80 text-sm md:text-base lg:text-lg max-w-md">
              Our proprietary smart meter attaches to any LPG cylinder, enabling real-time usage tracking and automated payments.
            </p>
            <ul className="mt-4 md:mt-6 space-y-2">
              {["GPRS Connectivity", "Tamper Alerts", "Auto Refill Scheduling"].map((feature, i) => (
                <li key={i} className="flex items-center gap-2 text-sm md:text-base text-white/90">
                  <Check className="w-3 h-3 md:w-4 md:h-4 text-gasify-orange" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
          <div className="absolute right-0 bottom-0 w-48 md:w-64 h-48 md:h-64 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3" />
        </motion.div>

        {/* Mobile Money Card */}
        <motion.div 
          variants={fadeUp}
          className="bg-gasify-orange-light rounded-2xl md:rounded-3xl p-6 md:p-8 group hover:shadow-xl transition-shadow"
        >
          <div className="w-12 h-12 md:w-14 md:h-14 bg-gasify-orange/20 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6">
            <Smartphone className="w-6 h-6 md:w-8 md:h-8 text-gasify-orange" />
          </div>
          <h3 className="font-heading text-lg md:text-xl font-semibold text-gasify-blue mb-2 md:mb-3">Mobile Money</h3>
          <p className="text-sm md:text-base text-slate-600">
            Pay in small amounts via MTN Mobile Money, Airtel Money, or any partner bank. 24/7 availability.
          </p>
        </motion.div>

        {/* Clean Energy Card */}
        <motion.div 
          variants={fadeUp}
          className="bg-slate-50 rounded-2xl md:rounded-3xl p-6 md:p-8 group hover:shadow-xl transition-shadow"
        >
          <div className="w-12 h-12 md:w-14 md:h-14 bg-gasify-blue/10 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6">
            <Flame className="w-6 h-6 md:w-8 md:h-8 text-gasify-blue" />
          </div>
          <h3 className="font-heading text-lg md:text-xl font-semibold text-gasify-blue mb-2 md:mb-3">Clean LPG</h3>
          <p className="text-sm md:text-base text-slate-600">
            Safe, efficient, and environmentally friendly cooking fuel delivered to your doorstep.
          </p>
        </motion.div>

        {/* Savings Card */}
        <motion.div 
          variants={fadeUp}
          className="md:col-span-2 bg-gasify-warm rounded-2xl md:rounded-3xl p-6 md:p-8 group hover:shadow-xl transition-shadow"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 md:gap-6">
            <div className="w-12 h-12 md:w-14 md:h-14 bg-gasify-orange/20 rounded-xl md:rounded-2xl flex items-center justify-center flex-shrink-0">
              <PiggyBank className="w-6 h-6 md:w-8 md:h-8 text-gasify-orange" />
            </div>
            <div>
              <h3 className="font-heading text-lg md:text-xl font-semibold text-gasify-blue mb-1 md:mb-2">No Upfront Costs</h3>
              <p className="text-sm md:text-base text-slate-600">
                Start cooking clean without buying expensive cylinders or stoves. We provide the equipment, you pay only for what you use.
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    { 
      number: "01", 
      title: "Sign Up", 
      description: "Register via our app or partner agents. Quick verification with your ID.",
      icon: Smartphone
    },
    { 
      number: "02", 
      title: "Get Kit Installed", 
      description: "We deliver and install your Gasify smart meter, cylinder, and stove.",
      icon: Cpu
    },
    { 
      number: "03", 
      title: "Pay via Mobile", 
      description: "Top up any amount via mobile money. Pay as little as UGX 1,000.",
      icon: PiggyBank
    },
    { 
      number: "04", 
      title: "Cook Clean", 
      description: "Enjoy safe, smoke-free cooking. Automatic refills when you run low.",
      icon: Flame
    }
  ];

  return (
    <section 
      id="how-it-works" 
      className="py-16 md:py-20 lg:py-32 bg-slate-50"
      data-testid="how-it-works-section"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-10 md:mb-16"
        >
          <motion.p 
            variants={fadeUp}
            className="text-xs md:text-sm text-gasify-orange uppercase tracking-widest font-semibold mb-3 md:mb-4"
          >
            How It Works
          </motion.p>
          <motion.h2 
            variants={fadeUp}
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-gasify-blue"
          >
            Get Started in 4 Simple Steps
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8"
        >
          {steps.map((step, index) => (
            <motion.div
              key={index}
              variants={fadeUp}
              className="relative"
            >
              {/* Connection Line */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-12 left-1/2 w-full h-0.5 bg-gasify-orange/20" />
              )}
              
              <div className="bg-white rounded-xl md:rounded-2xl p-5 md:p-8 shadow-sm hover:shadow-xl transition-all relative z-10 group">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-gasify-orange rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 group-hover:scale-110 transition-transform">
                  <step.icon className="w-6 h-6 md:w-8 md:h-8 text-white" />
                </div>
                <span className="text-4xl md:text-5xl font-bold text-slate-100 absolute top-3 md:top-4 right-3 md:right-4">{step.number}</span>
                <h3 className="font-heading text-lg md:text-xl font-semibold text-gasify-blue mb-2 md:mb-3">{step.title}</h3>
                <p className="text-sm md:text-base text-slate-600">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', organization: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Try backend API first (for Emergent deployment)
      // Falls back to PHP script (for cPanel deployment)
      const apiEndpoint = API ? `${API}/contact` : '/send-email.php';
      
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          organization: formData.organization,
          message: formData.message,
        }),
      });
      
      if (response.ok) {
        toast.success('Thank you! We will contact you soon.');
        setFormData({ name: '', email: '', organization: '', message: '' });
        setSubmitted(true);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  return (
    <section 
      id="contact" 
      className="py-16 md:py-20 lg:py-32 bg-gasify-blue"
      data-testid="contact-section"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Left Content */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-white text-center lg:text-left"
          >
            <motion.p 
              variants={fadeUp}
              className="text-xs md:text-sm text-gasify-orange uppercase tracking-widest font-semibold mb-3 md:mb-4"
            >
              Get In Touch
            </motion.p>
            <motion.h2 
              variants={fadeUp}
              className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-4 md:mb-6"
            >
              Let's Revolutionize <span className="text-gasify-orange">Clean Cooking</span> Together
            </motion.h2>
            <motion.p 
              variants={fadeUp}
              className="text-base md:text-lg text-slate-300 mb-6 md:mb-8"
            >
              Whether you're an investor, NGO, or potential distribution partner, we'd love to hear from you.
            </motion.p>

            <motion.div 
              variants={fadeUp}
              className="space-y-3 md:space-y-4 inline-block text-left"
            >
              <div className="flex items-center gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-white/10 rounded-lg md:rounded-xl flex items-center justify-center">
                  <Mail className="w-4 h-4 md:w-5 md:h-5 text-gasify-orange" />
                </div>
                <div>
                  <p className="text-xs md:text-sm text-slate-400">Email us</p>
                  <a href="mailto:info@gasifyenergy.com" className="text-sm md:text-base text-white font-medium hover:text-gasify-orange transition-colors">info@gasifyenergy.com</a>
                </div>
              </div>
              <div className="flex items-center gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-white/10 rounded-lg md:rounded-xl flex items-center justify-center">
                  <MapPin className="w-4 h-4 md:w-5 md:h-5 text-gasify-orange" />
                </div>
                <div>
                  <p className="text-xs md:text-sm text-slate-400">Location</p>
                  <p className="text-sm md:text-base text-white font-medium">Kampala, Uganda</p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl md:rounded-3xl p-5 md:p-8 shadow-2xl"
          >
            <h3 className="font-heading text-xl md:text-2xl font-semibold text-gasify-blue mb-4 md:mb-6">Partner With Us</h3>
            
            {submitted ? (
              <div className="text-center py-8 md:py-12">
                <div className="w-14 h-14 md:w-16 md:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <Check className="w-7 h-7 md:w-8 md:h-8 text-green-600" />
                </div>
                <h4 className="font-heading text-lg md:text-xl font-semibold text-gasify-blue mb-2">Message Sent!</h4>
                <p className="text-sm md:text-base text-slate-600">We'll get back to you shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
                <div>
                  <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1.5 md:mb-2">Full Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 md:px-5 py-3 md:py-3.5 bg-slate-50 border border-slate-200 rounded-lg md:rounded-xl focus:outline-none focus:ring-2 focus:ring-gasify-orange/20 focus:border-gasify-orange transition-all text-sm md:text-base"
                    placeholder="Starla Matiku"
                    required
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1.5 md:mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 md:px-5 py-3 md:py-3.5 bg-slate-50 border border-slate-200 rounded-lg md:rounded-xl focus:outline-none focus:ring-2 focus:ring-gasify-orange/20 focus:border-gasify-orange transition-all text-sm md:text-base"
                    placeholder="starlamatiku@gmail.com"
                    required
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1.5 md:mb-2">Organization</label>
                  <input
                    type="text"
                    value={formData.organization}
                    onChange={(e) => setFormData({...formData, organization: e.target.value})}
                    className="w-full px-4 md:px-5 py-3 md:py-3.5 bg-slate-50 border border-slate-200 rounded-lg md:rounded-xl focus:outline-none focus:ring-2 focus:ring-gasify-orange/20 focus:border-gasify-orange transition-all text-sm md:text-base"
                    placeholder="Your organization"
                    data-testid="input-organization"
                  />
                </div>
                <div>
                  <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1.5 md:mb-2">Message *</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    rows={3}
                    className="w-full px-4 md:px-5 py-3 md:py-3.5 bg-slate-50 border border-slate-200 rounded-lg md:rounded-xl focus:outline-none focus:ring-2 focus:ring-gasify-orange/20 focus:border-gasify-orange transition-all resize-none text-sm md:text-base"
                    placeholder="Tell us about your interest in partnering with Gasify..."
                    required
                    data-testid="input-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gasify-orange text-white py-3 md:py-4 rounded-full font-semibold hover:bg-gasify-orange-hover transition-all btn-primary-shadow flex items-center justify-center gap-2 disabled:opacity-50 text-sm md:text-base"
                  data-testid="submit-partner"
                >
                  {loading ? 'Sending...' : (
                    <>
                      Send Message
                      <Send className="w-4 h-4 md:w-5 md:h-5" />
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="bg-gasify-blue-hover py-8 md:py-12" data-testid="footer">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6">
        <img 
          src="https://customer-assets.emergentagent.com/job_cleanfuel-ug/artifacts/2wwu57y3_GASIFY_LOGO-2.png" 
          alt="Gasify Logo" 
          className="h-8 md:h-10 brightness-0 invert"
        />
        <p className="text-slate-400 text-center text-xs md:text-sm">
          © {new Date().getFullYear()} Gasify Energy. Clean cooking for every home.
        </p>
        <div className="flex gap-4 md:gap-6">
          <a href="#" className="text-xs md:text-sm text-slate-400 hover:text-gasify-orange transition-colors">Privacy</a>
          <a href="#" className="text-xs md:text-sm text-slate-400 hover:text-gasify-orange transition-colors">Terms</a>
        </div>
      </div>
    </div>
  </footer>
);

// Main App Component
function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <Navbar />
      <HeroSection />
      <ProblemSection />
      <SolutionSection />
      <HowItWorksSection />
      <ContactSection />
      <Footer />
    </div>
  );
}

export default App;
